import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { KeyRound, Plus, Sparkles } from 'lucide-react';
import type { CreateScopedSecretRequest, UpdateScopedSecretRequest } from '@reflex/shared';
import { Button } from '@reflex/ui/components/ui/button';
import {
  HowToUseParagraph,
  HowToUseSection,
  HowToUseStep,
  HowToUseSteps,
} from '@reflex/ui/components/integration/HowToUse';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { AddSecretPopover } from '@/components/secrets/AddSecretPopover';
import { ScopedSecretsManager } from '@/components/secrets/ScopedSecretsManager';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { ROUTES } from '@/lib/routes';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';

/**
 * "Secrets" — the user-tier general-purpose secrets surface, mounted in the
 * Security drill at `/{slug}/security/secrets` and backed by the `/me/secrets`
 * API. Sister page to the Security → Model Providers page: model providers
 * are typed login/OAuth blobs and provider keys, while this page hosts
 * arbitrary user-scoped key/value secrets that get mounted into the devbox
 * env when the user launches agents.
 *
 * ID-first: rows are referenced by `sec_*` id end-to-end (URL params,
 * agent env wiring, devbox mirroring). The `name` column is a
 * human-readable label scoped per-user — same name can appear under
 * different users.
 */
export function SecretsPage({ embedded = false }: { embedded?: boolean }) {
  const queryClient = useQueryClient();
  // Per-org user-tier secrets: the server scopes every read to the
  // session's active organization, so the cache slot must match. When
  // the operator switches orgs `useOrgKey()` returns a new value and
  // TanStack Query refetches from a clean slot — no flash of the
  // previous org's secrets.
  const orgKey = useOrgKey();
  const slug = useActiveOrgSlug();
  const { activeOrganization } = useOrganization();
  const userSecretsKey = queryKeys.userSecrets(orgKey);

  const secretsQuery = useQuery({
    queryKey: userSecretsKey,
    queryFn: api.listUserSecrets,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateScopedSecretRequest) => api.createUserSecret(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userSecretsKey });
      toast.success('Secret saved');
    },
    onError: (err: Error) => toast.error('Failed to save secret', { description: err.message }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ secretId, data }: { secretId: string; data: UpdateScopedSecretRequest }) =>
      api.updateUserSecret(secretId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userSecretsKey });
      toast.success('Secret updated');
    },
    onError: (err: Error) => toast.error('Failed to update secret', { description: err.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: (secretId: string) => api.deleteUserSecret(secretId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userSecretsKey });
      toast.success('Secret deleted');
    },
    onError: (err: Error) => toast.error('Failed to delete secret', { description: err.message }),
  });

  const isPending =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const orgName = activeOrganization?.name ?? activeOrganization?.slug ?? null;
  const description = orgName
    ? `Personal secrets for ${orgName}, mounted into every agent you launch as environment variables. Each organization keeps its own list, visible only to you.`
    : 'Personal secrets, mounted into every agent you launch as environment variables. Visible only to you.';

  const body = (
    <>
      {!embedded && (
        <PageHeader
          iconVariant="box"
          icon={<KeyRound className="h-5 w-5 text-muted-foreground" />}
          title="Secrets"
          description={description}
          docs="administration/security#secrets-tiers"
          actions={
            <div className="flex items-center gap-2">
              <AddSecretPopover
                onSubmit={(name, value) => createMutation.mutate({ name, value })}
                isPending={isPending}
                disabled={isPending}
                trigger={
                  <Button aria-label="Add secret">
                    <Plus />
                    <span className="sm:hidden">Add</span>
                    <span className="hidden sm:inline">Add secret</span>
                  </Button>
                }
              />
              {slug ? (
                <Button asChild variant="outline" size="sm">
                  <Link
                    to={ROUTES.org(slug).credentialsHubSection('personal', 'model-providers')}
                    aria-label="Model Providers"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    <span className="max-sm:hidden">Model Providers</span>
                  </Link>
                </Button>
              ) : null}
            </div>
          }
          howToUse={{
            label: 'How secrets work',
            children: (
              <>
                <HowToUseSection title="What they are">
                  <HowToUseParagraph>
                    Personal key/value secrets for this organization. Each value is stored encrypted
                    and injected as an environment variable when you launch an agent.
                  </HowToUseParagraph>
                </HowToUseSection>
                <HowToUseSection title="Who can see them">
                  <HowToUseSteps>
                    <HowToUseStep>
                      Only you can view and edit this list. Organization administrators cannot read
                      your personal secret values.
                    </HowToUseStep>
                    <HowToUseStep>
                      Switching organizations shows a different personal list. Secrets do not carry
                      across orgs.
                    </HowToUseStep>
                  </HowToUseSteps>
                </HowToUseSection>
              </>
            ),
          }}
        />
      )}

      <ScopedSecretsManager
        title="Personal Secrets"
        description="Add personal key/value secrets here for tokens you don't want to share with the rest of your organization."
        secrets={secretsQuery.data ?? []}
        isLoading={secretsQuery.isLoading}
        isPending={isPending}
        showSectionAction={embedded}
        emptyState="No personal secrets yet. Add one to make it available to every agent you launch."
        onCreate={(name, value) => createMutation.mutate({ name, value })}
        onUpdate={(secretId, value) => updateMutation.mutate({ secretId, data: { value } })}
        onDelete={(secretId) => deleteMutation.mutate(secretId)}
      />
    </>
  );

  if (embedded) return body;

  return <PageLayout maxWidth="3xl">{body}</PageLayout>;
}
